import { formatSplitterTime } from '@wajeez-web/core/utils';
import { Chapter } from './AudioSegmentManager';
import { EditChapterModal } from './EditChapterModal';
import { useCallback } from 'react';

interface ChapterSegment {
  chapter: Chapter;
  duration: number;
  sortedChapters: Chapter[];
  index: number;
  openModalId: number | null;
  handleOpenModal: (id: number) => void;
  isRtl: boolean;
  handleUpdateChapter: (updatedChapter: Chapter) => void;
  setChapters: React.Dispatch<React.SetStateAction<Chapter[]>>;
  setIsDragging: React.Dispatch<React.SetStateAction<boolean>>;
  onDeleteChapter: (id: number | undefined) => void;
}

const ChapterSegment = ({
  chapter,
  duration,
  sortedChapters,
  index,
  openModalId,
  handleOpenModal,
  isRtl,
  handleUpdateChapter,
  setChapters,
  setIsDragging,
  onDeleteChapter,
}: ChapterSegment) => {
  const prevChapterPoint = index > 0 ? sortedChapters[index - 1].point : 0;
  const segmentHeight = chapter.point - prevChapterPoint;

  const calculatePointFromPosition = useCallback(
    (clientY: number, containerRect: DOMRect) => {
      const containerHeight = containerRect.height;
      const relativeY = Math.max(
        0,
        Math.min(clientY - containerRect.top, containerHeight)
      );
      const percentage = (relativeY / containerHeight) * 100;
      return Math.max(0, Math.min(100, percentage));
    },
    []
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsDragging(true);

      const container = e.currentTarget.closest('.w-full.relative.ps-24');
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const initialPoint = calculatePointFromPosition(e.clientY, rect);

      setChapters((prev) => {
        const updatedChapters = prev.map((c, i) => {
          if (c.id) {
            return c.id === chapter.id ? { ...c, point: initialPoint } : c;
          }
          return i === index ? { ...c, point: initialPoint } : c;
        });

        const updatedChapter =
          updatedChapters.find((c) => c.id === chapter.id) ||
          updatedChapters[index];
        handleUpdateChapter(updatedChapter);

        return updatedChapters;
      });

      const handleMouseMove = (e: MouseEvent) => {
        const newPoint = calculatePointFromPosition(e.clientY, rect);

        setChapters((prev) => {
          const updatedChapters = prev.map((c, i) => {
            if (c.id) {
              return c.id === chapter.id ? { ...c, point: newPoint } : c;
            }
            return i === index ? { ...c, point: newPoint } : c;
          });

          const updatedChapter =
            updatedChapters.find((c) => c.id === chapter.id) ||
            updatedChapters[index];
          handleUpdateChapter(updatedChapter);

          return updatedChapters;
        });
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [calculatePointFromPosition, chapter.id, setChapters, setIsDragging]
  );

  return (
    <div key={chapter.point} className="group">
      {/* Chapter marker */}
      <div
        className={`absolute right-[12px] cursor-pointer`}
        style={{
          top: `${chapter.point}%`,
        }}
      >
        <div
          className={`flex justify-center gap-3 ${
            chapter.point === duration && 'hidden'
          } w-full`}
        >
          <div>{formatSplitterTime(chapter.point, duration)}</div>
          <div className="bg-blue-500 w-8 h-1" onMouseDown={handleMouseDown} />
          <EditChapterModal
            chapter={chapter}
            isOpen={openModalId === chapter.point}
            onOpen={() => handleOpenModal(chapter.point)}
            onClose={() => handleOpenModal(chapter.point)}
            isRtl={isRtl}
            onSave={handleUpdateChapter}
            onDelete={onDeleteChapter}
          />
        </div>
      </div>
      {/* Chapter segment line */}
      <div
        className={`bg-blue-500 w-2 absolute ${
          index === 0 && 'rounded-t-full'
        } ${chapter.point === 100 && 'rounded-b-full'}`}
        style={{
          top: `${prevChapterPoint}%`,
          height: `${segmentHeight}%`,
        }}
      />
    </div>
  );
};

export default ChapterSegment;
